import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../config/api';
import SchoolLayout from '../components/SchoolLayout';
import {
  addDays,
  findOverlapsForDay,
  formatDayLabel,
  formatMinutes,
  getBlockPosition,
  getCurrentTimePosition,
  getDayBlockLayout,
  getVisibleHourRange,
  startOfWeek,
} from '../lib/carpoolScheduleGrid';

// One hue per line, assigned by position so a line keeps its colour all week.
const LINE_COLORS = ['#2a78d6', '#1baf7a', '#7c3aed', '#b45309', '#0891b2', '#c2410c', '#4d7c0f', '#be123c'];

const SOURCE_LABELS = {
  calendar_event: 'Calendar event',
  calendar_no_school: 'No school',
  weekday_no_dismissal: 'No dismissal',
  line_absolute: 'Line time (fixed)',
  weekday_default: 'Weekday override',
  line: 'Line time',
  school_default: 'School default',
  none: 'No time set',
};

const SOURCE_HINTS = {
  calendar_event: 'A calendar event moved dismissal for this day.',
  line_absolute: 'This line ignores school and weekday defaults.',
  weekday_default: 'A weekday override for this day takes precedence over the line time.',
  line: 'This line\'s own dismissal time.',
  school_default: 'No line or weekday time is set, so the school default applies.',
  none: 'No dismissal time could be resolved for this line.',
};

const HOUR_HEIGHT = 44;

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

export default function CarpoolSchedulePage({ token, navigate, schoolId, user, bare = false }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(todayKey()));
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null); // { date, line }
  // The school's own clock. An administrator in another timezone must not see
  // the marker in the wrong place on a schedule enforced in school-local time.
  const [schoolNow, setSchoolNow] = useState(null);

  const load = useCallback(async () => {
    if (!schoolId || !weekStart) return;

    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(
        `/carpool-schedule?school_id=${schoolId}&from=${weekStart}&to=${addDays(weekStart, 6)}`,
        {},
        token
      );
      setDays(res.data || []);
    } catch (err) {
      setError(err.message || 'Could not load the carpool schedule');
      setDays([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token, weekStart]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!schoolId) return undefined;

    let cancelled = false;
    const readClock = () => {
      apiFetch(`/carpool-schedule/active?school_id=${schoolId}`, {}, token)
        .then((res) => {
          if (cancelled || !res.data) return;
          setSchoolNow({ date: res.data.date, minutes: res.data.school_minutes, readAt: Date.now() });
        })
        .catch(() => { if (!cancelled) setSchoolNow(null); });
    };

    readClock();
    // Re-anchor periodically; the marker advances locally between reads.
    const interval = window.setInterval(readClock, 5 * 60 * 1000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [schoolId, token]);

  // Advance the marker between server reads rather than freezing it for five
  // minutes. Holding the minutes in state keeps the dependency honest.
  const [nowMinutes, setNowMinutes] = useState(null);
  useEffect(() => {
    if (!schoolNow) {
      setNowMinutes(null);
      return undefined;
    }

    const advance = () => setNowMinutes(
      schoolNow.minutes + Math.floor((Date.now() - schoolNow.readAt) / 60000)
    );

    advance();
    const interval = window.setInterval(advance, 60000);
    return () => window.clearInterval(interval);
  }, [schoolNow]);

  // Keep the open panel in step with freshly loaded data.
  useEffect(() => {
    setSelected((current) => {
      if (!current) return current;
      const day = days.find((item) => item.date === current.date);
      const line = day?.lines?.find((item) => item.carpool_line_id === current.line.carpool_line_id);
      return line ? { date: current.date, line } : null;
    });
  }, [days]);

  const { startHour, endHour } = useMemo(() => getVisibleHourRange(days), [days]);
  const hours = useMemo(
    () => Array.from({ length: endHour - startHour }, (_, index) => startHour + index),
    [endHour, startHour]
  );

  const colorByLineId = useMemo(() => {
    const ids = [...new Set(days.flatMap((day) => (day.lines || []).map((line) => line.carpool_line_id)))];
    return new Map(ids.map((id, index) => [id, LINE_COLORS[index % LINE_COLORS.length]]));
  }, [days]);

  const legendLines = useMemo(() => {
    const seen = new Map();
    days.forEach((day) => (day.lines || []).forEach((line) => {
      if (!seen.has(line.carpool_line_id)) seen.set(line.carpool_line_id, line);
    }));
    return [...seen.values()];
  }, [days]);

  const nowPosition = useMemo(
    () => getCurrentTimePosition({
      days,
      todayKey: schoolNow?.date,
      nowMinutes,
      startHour,
      endHour,
    }),
    [days, endHour, nowMinutes, schoolNow, startHour]
  );

  const conflictDays = useMemo(
    () => days.filter((day) => !day.no_school && findOverlapsForDay(day).length > 0),
    [days]
  );

  const selectedConflicts = useMemo(() => {
    if (!selected) return [];
    const day = days.find((item) => item.date === selected.date);
    if (!day) return [];
    return findOverlapsForDay(day)
      .filter((pair) => pair.some((line) => line.carpool_line_id === selected.line.carpool_line_id))
      .map((pair) => pair.find((line) => line.carpool_line_id !== selected.line.carpool_line_id))
      .filter(Boolean);
  }, [days, selected]);

  const weekLabel = weekStart
    ? `${formatDayLabel(weekStart)} – ${formatDayLabel(addDays(weekStart, 6))}`
    : '';

  const content = (
    <div className="sap-page">
      {error ? <div className="sa-error sap-inline-error">{error}</div> : null}

      {conflictDays.length > 0 && (
        <div className="spz-warning" style={{ marginBottom: 14 }}>
          <strong>Two pickup lines run at the same time on {conflictDays.map((day) => formatDayLabel(day.date)).join(', ')}.</strong>
          {' '}
          If those lines cover the same area, one parent arriving raises a pickup
          event on each and students get called repeatedly. Give each line its own
          pickup window in Pickup Zones, or make sure their zones do not overlap.
        </div>
      )}

      {loading ? (
        <div className="sa-empty">Loading...</div>
      ) : (
        <div className="scs-shell">
          <section className="sap-parents-panel scs-week-panel">
            <div className="sap-panel-heading">
              <div>
                <h2>Carpool Schedule</h2>
                <p>When each pickup line runs, after school defaults, weekday overrides, and calendar events</p>
              </div>
              <div className="scs-week-controls">
                <button className="sa-btn sa-btn-secondary" onClick={() => setWeekStart(addDays(weekStart, -7))}>←</button>
                <button className="sa-btn sa-btn-secondary" onClick={() => setWeekStart(startOfWeek(todayKey()))}>This week</button>
                <button className="sa-btn sa-btn-secondary" onClick={() => setWeekStart(addDays(weekStart, 7))}>→</button>
              </div>
            </div>

            <div className="scs-week-label">{weekLabel}</div>

            {legendLines.length > 0 && (
              <div className="scs-legend">
                {legendLines.map((line) => (
                  <span key={line.carpool_line_id} className="scs-legend-item">
                    <span
                      className="scs-legend-swatch"
                      style={{
                        background: `${colorByLineId.get(line.carpool_line_id)}33`,
                        borderColor: colorByLineId.get(line.carpool_line_id),
                      }}
                    />
                    {line.name}
                    {!line.geofenced && <span className="scs-legend-note">no pickup zone</span>}
                  </span>
                ))}
              </div>
            )}

            <div className="scs-grid-scroll">
              <div className="scs-grid">
                <div className="scs-corner" />
                {days.map((day) => (
                  <div key={day.date} className={`scs-day-head ${day.no_school ? 'no-school' : ''}`}>
                    {formatDayLabel(day.date)}
                    {day.no_school && <span className="scs-day-head-note">{day.no_school_reason || 'No school'}</span>}
                  </div>
                ))}

                <div className="scs-hours">
                  {hours.map((hour) => (
                    <div key={hour} className="scs-hour" style={{ height: HOUR_HEIGHT }}>
                      {formatMinutes(hour * 60)}
                    </div>
                  ))}
                </div>

                {days.map((day, dayIndex) => (
                  <div
                    key={day.date}
                    className={`scs-day-col ${day.no_school ? 'no-school' : ''}`}
                    style={{ height: hours.length * HOUR_HEIGHT }}
                  >
                    {hours.map((hour) => (
                      <div key={hour} className="scs-gridline" style={{ top: (hour - startHour) * HOUR_HEIGHT }} />
                    ))}

                    {nowPosition && nowPosition.columnIndex === dayIndex ? (
                    <div className="scs-now" style={{ top: `${nowPosition.top}%` }}>
                      <span className="scs-now-dot" />
                      <span className="scs-now-label">{nowPosition.label}</span>
                    </div>
                  ) : null}

                  {(() => {
                      // Concurrent lines share the column instead of stacking.
                      const blockLayout = getDayBlockLayout(day.lines);

                      return (day.lines || []).map((line) => {
                        const position = getBlockPosition(line, startHour, endHour);
                        if (!position) return null;

                        const color = colorByLineId.get(line.carpool_line_id);
                        const isSelected = selected?.date === day.date
                          && selected?.line.carpool_line_id === line.carpool_line_id;
                        const { columnIndex, columnCount } = blockLayout.get(line.carpool_line_id)
                          || { columnIndex: 0, columnCount: 1 };
                        const columnWidth = 100 / columnCount;
                        const split = columnCount > 1;

                        return (
                          <button
                            key={line.carpool_line_id}
                            type="button"
                            className={`scs-block ${isSelected ? 'selected' : ''} ${position.bounded ? '' : 'unbounded'} ${split ? 'split' : ''}`}
                            style={{
                              top: `${position.top}%`,
                              height: `${position.height}%`,
                              // left/width rather than left/right, so a block can
                              // take its share of the column.
                              left: `calc(${columnIndex * columnWidth}% + 4px)`,
                              width: `calc(${columnWidth}% - 8px)`,
                              right: 'auto',
                              background: `${color}26`,
                              borderColor: color,
                            }}
                            onClick={() => setSelected({ date: day.date, line })}
                            title={split ? `${line.name} · ${position.label}` : undefined}
                          >
                            <span className="scs-block-name">{line.name}</span>
                            <span className="scs-block-time">{formatMinutes(line.dismissal_minutes)}</span>
                          </button>
                        );
                      });
                    })()}
                  </div>
                ))}
              </div>
            </div>

            {legendLines.length === 0 && (
              <div className="sa-empty sap-compact-empty">This school has no active pickup lines.</div>
            )}
          </section>

          {/* Slot details — what set this time, and what to do about it. */}
          <section className="sap-parents-panel scs-detail-panel">
            <div className="sap-panel-heading">
              <div>
                <h2>{selected ? selected.line.name : 'Slot details'}</h2>
                <p>{selected ? formatDayLabel(selected.date) : 'Select a block to see how its time was decided'}</p>
              </div>
            </div>

            {!selected ? (
              <div className="sa-empty sap-compact-empty">
                Click any block in the week to see which rule set its time and adjust it.
              </div>
            ) : (
              <div className="scs-detail">
                <div className="scs-detail-row">
                  <span className="scs-detail-label">Dismissal</span>
                  <span className="scs-detail-value">{formatMinutes(selected.line.dismissal_minutes)}</span>
                </div>
                <div className="scs-detail-row">
                  <span className="scs-detail-label">Set by</span>
                  <span className="scs-detail-value">{SOURCE_LABELS[selected.line.source] || selected.line.source}</span>
                </div>
                <p className="scs-detail-hint">{SOURCE_HINTS[selected.line.source] || ''}</p>

                <div className="scs-detail-row">
                  <span className="scs-detail-label">Pickup window</span>
                  <span className="scs-detail-value">
                    {selected.line.active_duration_minutes
                      ? `${formatMinutes(selected.line.window_start_minutes)} – ${formatMinutes(selected.line.window_end_minutes)}`
                      : 'Live all day'}
                  </span>
                </div>
                <p className="scs-detail-hint">
                  {selected.line.active_duration_minutes
                    ? `The pickup zone is only live for these ${selected.line.active_duration_minutes} minutes (plus 10 minutes early for parents already waiting).`
                    : 'No window is set, so this line\'s pickup zone is live whenever the school is running.'}
                </p>

                <div className="scs-detail-row">
                  <span className="scs-detail-label">Pickup zone</span>
                  <span className="scs-detail-value">{selected.line.geofenced ? 'Enabled' : 'None'}</span>
                </div>

                {selectedConflicts.length > 0 && (
                  <div className="spz-warning scs-detail-warning">
                    <strong>Runs at the same time as {selectedConflicts.map((line) => line.name).join(', ')}.</strong>
                    {' '}
                    If these lines cover the same area, one arrival raises a pickup
                    event on each.
                  </div>
                )}

                <div className="scs-detail-actions">
                  <button
                    className="sa-btn sa-btn-secondary"
                    onClick={() => navigate('pickup-zones', schoolId)}
                  >
                    Edit line &amp; window
                  </button>
                  <button
                    className="sa-btn sa-btn-secondary"
                    onClick={() => navigate('calendar', schoolId)}
                  >
                    School calendar
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );

  // App renders school pages inside a single full-bleed SchoolLayout; wrapping
  // our own would nest the school shell inside the super-admin chrome.
  if (bare) return content;

  return (
    <SchoolLayout
      token={token}
      navigate={navigate}
      schoolId={schoolId}
      user={user}
      activePage="carpool-schedule"
      breadcrumb="Carpool Schedule"
    >
      {content}
    </SchoolLayout>
  );
}
